import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { FuaFormField } from 'components/Assessments/FuaAssessment/types/FuaFormField'
import { MedicalBackgroundQuestionnaireItem } from 'components/Assessments/FuaAssessment/sections/MedicalBackground/questionnaire'
import { PhysicianNotificationFormField } from './formField'

/**
 * Physician Notification subsection adapter
 * Handles physician notification thresholds with nested less/greater pairs (14 fields)
 * This handles vital sign thresholds that trigger physician notifications
 */
export class PhysicianNotificationAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Temperature thresholds
    const tempLess = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_TEMP_LESS
    )?.at(0)

    const tempGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_TEMP_GREATER
    )?.at(0)

    // Pulse thresholds
    const pulseLess = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_PULSE_LESS
    )?.at(0)

    const pulseGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_PULSE_GREATER
    )?.at(0)

    // Respirations thresholds
    const respirationsLess = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_RESPIRATIONS_LESS
    )?.at(0)

    const respirationsGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_RESPIRATIONS_GREATER
    )?.at(0)

    // Blood pressure thresholds
    const bpLess = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_BP_LESS
    )?.at(0)

    const bpGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_BP_GREATER
    )?.at(0)

    // Weight thresholds
    const weightLess = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_WEIGHT_LESS
    )?.at(0)

    const weightGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_WEIGHT_GREATER
    )?.at(0)

    // EtCO2 saturation thresholds
    const etco2SaturationLess = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_LESS
    )?.at(0)

    const etco2SaturationGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_GREATER
    )?.at(0)

    // O2 saturation thresholds
    const o2SaturationLess = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_O2_SATURATION_LESS
    )?.at(0)

    const o2SaturationGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_O2_SATURATION_GREATER
    )?.at(0)

    return {
      [PhysicianNotificationFormField.PHYSICIAN_NOTIFICATION_TEMP_LESS]: tempLess,
      [PhysicianNotificationFormField.PHYSICIAN_NOTIFICATION_TEMP_GREATER]: tempGreater,
      [PhysicianNotificationFormField.PHYSICIAN_NOTIFICATION_PULSE_LESS]: pulseLess,
      [PhysicianNotificationFormField.PHYSICIAN_NOTIFICATION_PULSE_GREATER]: pulseGreater,
      [PhysicianNotificationFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_LESS]: respirationsLess,
      [PhysicianNotificationFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_GREATER]:
        respirationsGreater,
      [PhysicianNotificationFormField.PHYSICIAN_NOTIFICATION_BP_LESS]: bpLess,
      [PhysicianNotificationFormField.PHYSICIAN_NOTIFICATION_BP_GREATER]: bpGreater,
      [PhysicianNotificationFormField.PHYSICIAN_NOTIFICATION_WEIGHT_LESS]: weightLess,
      [PhysicianNotificationFormField.PHYSICIAN_NOTIFICATION_WEIGHT_GREATER]: weightGreater,
      [PhysicianNotificationFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_LESS]:
        etco2SaturationLess,
      [PhysicianNotificationFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_GREATER]:
        etco2SaturationGreater,
      [PhysicianNotificationFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_LESS]: o2SaturationLess,
      [PhysicianNotificationFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_GREATER]:
        o2SaturationGreater
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    return [
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_TEMP_LESS,
        value: this.getFormDataField(FuaFormField.PHYSICIAN_NOTIFICATION_TEMP_LESS)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_TEMP_GREATER,
        value: this.getFormDataField(FuaFormField.PHYSICIAN_NOTIFICATION_TEMP_GREATER)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_PULSE_LESS,
        value: this.getFormDataField(FuaFormField.PHYSICIAN_NOTIFICATION_PULSE_LESS)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_PULSE_GREATER,
        value: this.getFormDataField(FuaFormField.PHYSICIAN_NOTIFICATION_PULSE_GREATER)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_RESPIRATIONS_LESS,
        value: this.getFormDataField(FuaFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_LESS)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_RESPIRATIONS_GREATER,
        value: this.getFormDataField(FuaFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_GREATER)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_BP_LESS,
        value: this.getFormDataField(FuaFormField.PHYSICIAN_NOTIFICATION_BP_LESS)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_BP_GREATER,
        value: this.getFormDataField(FuaFormField.PHYSICIAN_NOTIFICATION_BP_GREATER)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_WEIGHT_LESS,
        value: this.getFormDataField(FuaFormField.PHYSICIAN_NOTIFICATION_WEIGHT_LESS)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_WEIGHT_GREATER,
        value: this.getFormDataField(FuaFormField.PHYSICIAN_NOTIFICATION_WEIGHT_GREATER)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_LESS,
        value: this.getFormDataField(FuaFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_LESS)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_GREATER,
        value: this.getFormDataField(FuaFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_GREATER)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_O2_SATURATION_LESS,
        value: this.getFormDataField(FuaFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_LESS)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_O2_SATURATION_GREATER,
        value: this.getFormDataField(FuaFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_GREATER)
      }
    ]
  }
}
